/*
 * Sticky add-to-cart bar.
 * - Slides in once the main buy buttons scroll out of view.
 * - Its Add to cart button submits the main product form (via the form= attribute),
 *   so Dawn's product-form.js handles the AJAX add + cart drawer/notification.
 * - Keeps the variant <select>, quantity, price and availability in sync with the
 *   main variant picker in both directions.
 */
(function () {
  class ProductStickyBar extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId;
      this.sectionId = this.dataset.sectionId;
      this.productInfo = this.closest('product-info') || document;

      this.variantSelect = this.querySelector('[data-sticky-variant]');
      this.qtyInput = this.querySelector('[data-sticky-qty]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.imageEl = this.querySelector('[data-sticky-image]');
      this.addBtn = this.querySelector('[data-sticky-add]');
      this.addText = this.querySelector('[data-sticky-add-text]');

      this.variants = this.parseVariants();
      this.mainVariantSelects = document.getElementById('variant-selects-' + this.sectionId);
      this.mainForm = document.getElementById(this.formId);
      this.mainQty = this.mainForm ? this.mainForm.querySelector('.quantity__input, [name="quantity"]') : null;
      this.mainPrice = document.getElementById('price-' + this.sectionId);
      this.mainSubmit = document.getElementById('ProductSubmitButton-' + this.sectionId);

      this.bindVisibility();
      this.bindVariantSync();
      this.bindQuantity();
      this.mirrorPrice();
      this.mirrorSubmit();
    }

    parseVariants() {
      var node = this.querySelector('[data-sticky-variants]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return [];
      }
    }

    /* --- Show / hide on scroll --- */
    bindVisibility() {
      var anchor = this.mainSubmit || (this.mainForm && this.mainForm.querySelector('.product-form__buttons'));
      if (!anchor || !('IntersectionObserver' in window)) return;

      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            // Visible once the buy buttons have scrolled above the viewport.
            var scrolledPast = entry.boundingClientRect.top < 0;
            self.toggle(!entry.isIntersecting && scrolledPast);
          });
        },
        { rootMargin: '0px 0px 0px 0px', threshold: 0 }
      );
      this.observer.observe(anchor);
    }

    toggle(show) {
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    /* --- Variant sync --- */
    bindVariantSync() {
      var self = this;

      // Sticky -> main
      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', function () {
          self.pushVariantToMain(self.variantSelect.value);
          self.updateImage(self.variantSelect.value);
        });
      }

      // Main -> sticky (recompute from the main picker on any change)
      if (this.mainVariantSelects) {
        this.mainVariantSelects.addEventListener('change', function () {
          // Defer so Dawn finishes updating its own state first.
          window.setTimeout(function () {
            var variant = self.getSelectedMainVariant();
            if (!variant) return;
            if (self.variantSelect) self.variantSelect.value = variant.id;
            self.updateImage(variant.id);
          }, 0);
        });
      }
    }

    getSelectedMainVariant() {
      if (!this.mainVariantSelects) return this.variants[0];
      var chosen = [];
      this.mainVariantSelects.querySelectorAll('.product-form__input').forEach(function (group) {
        var select = group.querySelector('select');
        if (select) {
          chosen.push(select.value);
          return;
        }
        var checked = group.querySelector('input[type="radio"]:checked');
        if (checked) chosen.push(checked.value);
      });
      return this.variants.find(function (v) {
        return v.options.length === chosen.length && v.options.every(function (opt, i) { return opt === chosen[i]; });
      });
    }

    pushVariantToMain(variantId) {
      var variant = this.variants.find(function (v) { return String(v.id) === String(variantId); });
      if (!variant || !this.mainVariantSelects) return;

      var groups = this.mainVariantSelects.querySelectorAll('.product-form__input');
      var lastChanged = null;
      groups.forEach(function (group, i) {
        var value = variant.options[i];
        if (value == null) return;
        var select = group.querySelector('select');
        if (select) {
          if (select.value !== value) select.value = value;
          lastChanged = select;
          return;
        }
        var radio = group.querySelector('input[type="radio"][value="' + CSS.escape(value) + '"]');
        if (radio && !radio.checked) {
          radio.checked = true;
          lastChanged = radio;
        } else if (radio) {
          lastChanged = lastChanged || radio;
        }
      });

      // One bubbling change event is enough — Dawn reads all option values from it.
      if (lastChanged) {
        lastChanged.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateImage(variantId) {
      if (!this.imageEl) return;
      var variant = this.variants.find(function (v) { return String(v.id) === String(variantId); });
      if (variant && variant.featured_image && variant.featured_image.src) {
        var src = variant.featured_image.src;
        this.imageEl.src = src.indexOf('width=') === -1
          ? src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=120'
          : src;
      }
    }

    /* --- Quantity sync --- */
    bindQuantity() {
      var self = this;
      if (!this.qtyInput) return;

      var syncToMain = function () {
        var val = Math.max(1, parseInt(self.qtyInput.value, 10) || 1);
        self.qtyInput.value = val;
        if (self.mainQty) {
          self.mainQty.value = val;
          self.mainQty.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };

      this.qtyInput.addEventListener('change', syncToMain);
      var minus = this.querySelector('[data-sticky-qty-minus]');
      var plus = this.querySelector('[data-sticky-qty-plus]');
      if (minus) minus.addEventListener('click', function () {
        self.qtyInput.value = Math.max(1, (parseInt(self.qtyInput.value, 10) || 1) - 1);
        syncToMain();
      });
      if (plus) plus.addEventListener('click', function () {
        self.qtyInput.value = (parseInt(self.qtyInput.value, 10) || 1) + 1;
        syncToMain();
      });

      // Main -> sticky
      if (this.mainQty) {
        this.mainQty.addEventListener('change', function () {
          var val = Math.max(1, parseInt(self.mainQty.value, 10) || 1);
          if (String(val) !== self.qtyInput.value) self.qtyInput.value = val;
        });
      }
    }

    /* --- Mirror price from the main product info ---
       The main price block renders with show_badges: true (Sale/Sold out pills).
       The sticky bar already shows availability via its own Add to cart button
       text/disabled state, so strip badges here to avoid a redundant, cramped
       "Sold out" pill breaking the compact price row's layout. */
    mirrorPrice() {
      if (!this.mainPrice || !this.priceEl || !('MutationObserver' in window)) return;
      var self = this;
      var sync = function () {
        var clone = self.mainPrice.cloneNode(true);
        clone.querySelectorAll('.badge').forEach(function (badge) { badge.remove(); });
        self.priceEl.innerHTML = clone.innerHTML;
      };
      sync();
      new MutationObserver(sync).observe(this.mainPrice, { childList: true, subtree: true });
    }

    mirrorSubmit() {
      if (!this.mainSubmit || !this.addBtn || !('MutationObserver' in window)) return;
      var self = this;
      var sync = function () {
        var disabled = self.mainSubmit.hasAttribute('disabled');
        self.addBtn.toggleAttribute('disabled', disabled);
        var mainText = self.mainSubmit.querySelector('span:not(.hidden)') || self.mainSubmit.querySelector('span');
        if (mainText && self.addText) self.addText.textContent = mainText.textContent.trim();
      };
      sync();
      new MutationObserver(sync).observe(this.mainSubmit, { attributes: true, childList: true, subtree: true });
    }
  }

  if (!customElements.get('product-sticky-bar')) {
    customElements.define('product-sticky-bar', ProductStickyBar);
  }
})();
